import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

export default function Contact() {
  const { ref: titleRef, isVisible: titleVisible } = useScrollAnimation<HTMLDivElement>();
  const { ref: cardsRef, isVisible: cardsVisible } = useScrollAnimation<HTMLDivElement>({ threshold: 0.1 });
  const { ref: ctaRef, isVisible: ctaVisible } = useScrollAnimation<HTMLDivElement>({ threshold: 0.1 });

  const contactMethods = [
    {
      icon: 'ri-phone-line',
      title: 'Telefone',
      description: 'Ligue para nós',
      info: ['(41) 3344-2203'],
      action: 'tel:+554133442203',
      actionText: 'Ligar Agora'
    },
    {
      icon: 'ri-mail-line',
      title: 'E-mail',
      description: 'Envie uma mensagem',
      info: ['contato@personapro.com.br'],
      action: 'mailto:contato@personapro.com.br',
      actionText: 'Enviar E-mail'
    },
    {
      icon: 'ri-whatsapp-line',
      title: 'WhatsApp',
      description: 'Atendimento rápido',
      info: ['(41) 3344-2203', 'Resposta em até 2h'],
      action: 'https://wa.me/554133442203',
      actionText: 'Iniciar Conversa'
    },
    {
      icon: 'ri-map-pin-line',
      title: 'Endereço',
      description: 'Visite nosso escritório',
      info: ['Rua Coronel Herculano de Araujo, nº 417', 'Novo Mundo, Curitiba - PR', 'CEP: 81050-090'],
      action: 'https://maps.google.com/?q=Rua+Coronel+Herculano+de+Araujo,+417,+Curitiba,+PR',
      actionText: 'Ver no Mapa'
    }
  ];

  return (
    <section id="contato" className="py-24 bg-gradient-to-b from-white to-gray-50">
      <div className="w-full px-6">
        <div className="max-w-[90%] mx-auto">
          <div 
            ref={titleRef}
            className={`text-center mb-16 transition-all duration-1000 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-8'}`}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Entre em <span className="text-[#D4AF37]">Contato</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Estamos prontos para ajudar sua empresa a alcançar novos patamares através da gestão estratégica de pessoas. Escolha o canal de sua preferência!
            </p>
          </div>

          <div 
            ref={cardsRef}
            className={`grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16 transition-all duration-1000 ${cardsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}
          >
            {contactMethods.map((method, index) => (
              <div 
                key={method.title}
                className="bg-white rounded-lg p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 group"
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className="w-16 h-16 bg-gradient-to-br from-[#D4AF37] to-[#B8941F] rounded-lg flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300">
                  <i className={`${method.icon} text-2xl text-white`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-1">{method.title}</h3>
                <p className="text-sm text-gray-500 mb-4">{method.description}</p>
                <div className="space-y-1 mb-5">
                  {method.info.map((line, i) => (
                    <p key={i} className="text-gray-700 text-sm">{line}</p>
                  ))}
                </div>
                <a 
                  href={method.action}
                  target={method.action.startsWith('http') ? '_blank' : undefined}
                  rel={method.action.startsWith('http') ? 'noopener noreferrer nofollow' : undefined}
                  className="inline-flex items-center text-[#D4AF37] font-semibold text-sm hover:text-[#B8941F] transition-colors cursor-pointer"
                >
                  {method.actionText}
                  <i className="ri-arrow-right-line ml-2"></i>
                </a>
              </div>
            ))}
          </div>

          <div 
            ref={ctaRef}
            className={`grid md:grid-cols-2 gap-8 transition-all duration-1000 delay-300 ${ctaVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}
          >
            <div className="bg-gradient-to-br from-[#D4AF37] to-[#B8941F] rounded-lg p-8 text-white">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center mr-4">
                  <i className="ri-calendar-check-line text-2xl"></i>
                </div>
                <div>
                  <h3 className="text-2xl font-bold">Consultoria Gratuita</h3>
                  <p className="text-white/80 text-sm">Sem compromisso</p>
                </div>
              </div>
              <p className="text-white/90 mb-6">
                Agende uma conversa com nossos especialistas e descubra como podemos transformar a gestão de pessoas da sua empresa com soluções personalizadas.
              </p>
              <div className="flex flex-wrap gap-4">
                <a 
                  href="https://wa.me/554133442203?text=Olá! Gostaria de agendar uma consultoria gratuita."
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="bg-white text-[#D4AF37] px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-whatsapp-line mr-2"></i>
                  Agendar pelo WhatsApp
                </a>
              </div>
            </div>

            <div className="bg-gray-900 rounded-lg p-8 text-white">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-[#D4AF37]/20 rounded-lg flex items-center justify-center mr-4">
                  <i className="ri-time-line text-2xl text-[#D4AF37]"></i>
                </div>
                <div>
                  <h3 className="text-2xl font-bold">Horário de Atendimento</h3>
                  <p className="text-gray-400 text-sm">Estamos disponíveis</p>
                </div>
              </div>
              <div className="space-y-4">
                <div className="flex justify-between items-center py-3 border-b border-gray-700">
                  <span className="text-gray-300">Segunda a Sexta</span>
                  <span className="font-semibold text-[#D4AF37]">8h às 18h</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-gray-700">
                  <span className="text-gray-300">Sábado</span>
                  <span className="text-gray-500">Fechado</span>
                </div>
                <div className="flex justify-between items-center py-3">
                  <span className="text-gray-300">Domingo e Feriados</span>
                  <span className="text-gray-500">Fechado</span>
                </div>
              </div>
              <div className="mt-6 flex items-center text-sm text-gray-400">
                <i className="ri-information-line mr-2"></i>
                Atendimento via WhatsApp 24h para urgências
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
